import type { FC } from "react";
import React, { useRef, useState } from "react";

import { Backdrop, Link } from "@mui/material";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import { Link as RouterLink } from "react-router-dom";

import ChatIcon from "assets/icons/colored/chat-bubble.svg?react";
import MobileMenuIcon from "assets/icons/filled/mobile-menu.svg?react";
import MagnifierIcon from "assets/icons/outlined/magnifier.svg?react";
import AvatarImgUrl from "assets/images/avatar.webp";
import DiceIconUrl from "assets/images/dice.webp";

import { getPaths } from "core/getPaths";
import { useUser } from "shared/hooks";

import styles from "./Header.module.scss";

import WalletBalance from "./WalletBalance";

import BalancePopover from "./WalletBalance/BalancePopover";

import Button, { ButtonTypes } from "../Button";

const { profile: profileRoutes, member: memberRoutes } = getPaths();

interface HeaderProps {
	onOpenAuthModal: (flow: "bonus" | "login" | "signUp") => void;
	onOpenGameSearchModal: () => void;
	onToggleMenu: () => void;
}

const Header: FC<HeaderProps> = ({ onOpenAuthModal, onOpenGameSearchModal, onToggleMenu }) => {
	const { t } = useTranslation();

	const { getData: getUserData } = useUser();
	const { isAuthorized } = getUserData();

	const balanceWrapper = useRef<null | HTMLDivElement>(null);

	const [showBalancePopover, setShowBalancePopover] = useState(false);

	const signUpNewUser = () => {
		onOpenAuthModal("bonus");
	};

	const loginUser = () => {
		onOpenAuthModal("login");
	};

	const toggleBalance = (wrapperRef?: React.RefObject<null | HTMLDivElement>) => {
		setShowBalancePopover(show => !show);
		if (wrapperRef) {
			balanceWrapper.current = wrapperRef!.current;
		}
	};

	return (
		<>
			<header
				className={clsx("flex items-center justify-between px-2 sm:px-4", showBalancePopover && "relative z-[1301]")}
			>
				<div className="flex items-center gap-6">
					<Link to={memberRoutes.home.getPath()} component={RouterLink} className="no-underline cursor-pointer">
						<img src={DiceIconUrl} alt="Dice" className="w-10" />
					</Link>
					<div className="w-[260px] max-sm:hidden">
						<Button
							type="input"
							placeholder={t("search")}
							onClick={onOpenGameSearchModal}
							readonly
							className="[&_.input]:cursor-pointer"
							endIcon={
								<div className={styles.searchIconWrapper}>
									<MagnifierIcon />
								</div>
							}
						/>
					</div>
				</div>
				<div className="flex items-center gap-[12px]">
					{!isAuthorized ? (
						<>
							<div className="w-[120px]">
								<Button onClick={signUpNewUser} variant={ButtonTypes.GOLD_BORD_FILL}>
									{t("sign_up")}
								</Button>
							</div>
							<div className="w-[120px]">
								<Button onClick={loginUser}>{t("login")}</Button>
							</div>
						</>
					) : (
						<>
							<WalletBalance onToggleBalance={toggleBalance} />
							<Link to={profileRoutes.dashboard.getPath()} component={RouterLink} className="no-underline w-11">
								<Button btnContentClassName="p-0 h-[34px]">
									<div className="w-[38px] h-[34px] rounded-full overflow-hidden translate-y-[3px] -translate-x-px">
										<img src={AvatarImgUrl} alt="Avatar" className="w-full" />
									</div>
								</Button>
							</Link>
							<div className="w-11">
								<Button variant={ButtonTypes.FILL}>
									<ChatIcon />
								</Button>
							</div>
						</>
					)}
					<Button variant={ButtonTypes.FILL} className="sm:hidden w-11" onClick={onToggleMenu}>
						<MobileMenuIcon />
					</Button>
				</div>
			</header>
			<Backdrop
				open={showBalancePopover}
				sx={{
					backgroundColor: "rgba(0, 0, 0, 0.75)",
					zIndex: 1300
				}}
			/>
			<BalancePopover show={showBalancePopover} balanceWrapper={balanceWrapper} onClose={toggleBalance} />
		</>
	);
};

export default Header;
